import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CreditCard, Check, ChevronRight, Zap, Play, BarChart2,
  Shield, Star, X, RefreshCw, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

const PLANS = [
  {
    id: 'creator',
    name: 'Creator',
    price: 29,
    period: 'month',
    description: 'Perfect for individual creators',
    features: [
      '1 Creator, up to 3 Brands',
      '50 GB Drive storage',
      'YouTube Clips + Talking Camera',
      'Reels Creator',
      'Risk Protector',
      'Publishing Hub (5 accounts)',
      'Viral Engine (daily ideas)',
      'No watermark',
    ],
    highlight: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 79,
    period: 'month',
    description: 'For serious creators & small teams',
    features: [
      'Up to 5 Creators, unlimited Brands',
      '500 GB Drive storage',
      'All creation tools + Viral Testing',
      'Publishing Hub (20 accounts)',
      'A/B Testing System',
      'Risk Protector + Auto-fix',
      'Priority support',
      'No watermark',
    ],
    highlight: true,
  },
  {
    id: 'agency',
    name: 'Agency',
    price: 199,
    period: 'month',
    description: 'Built for full-scale agencies',
    features: [
      'Unlimited Creators & Brands',
      '5 TB Drive storage',
      'All Pro features',
      'Unlimited social accounts',
      'White-label exports',
      'Team collaboration tools',
      'Dedicated account manager',
      'API access',
    ],
    highlight: false,
  },
];

export default function Billing() {
  const { subscription, setSubscription, trialActive, trialDaysLeft } = useApp();
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [billing, setBilling] = useState('monthly');
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);

  const handleUpgrade = async () => {
    try {
      await setSubscription(selectedPlan);
      setUpgradeOpen(false);
      setWatermarkEnabled(false);
      toast.success(`Upgraded to ${PLANS.find(p => p.id === selectedPlan)?.name} plan!`);
    } catch (e) {
      toast.error('Subscription failed. Please try again.');
    }
  };

  const currentPlan = PLANS.find(p => p.id === subscription) || null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Trial status */}
      {trialActive && !subscription && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl"
          style={{ background: 'hsl(var(--warning) / 0.08)', border: '1px solid hsl(var(--warning) / 0.3)' }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold" style={{ color: 'hsl(var(--warning))' }}>
                Free Trial — {trialDaysLeft} days remaining
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--foreground-muted))' }}>
                All exports include the Reelia watermark during trial. Upgrade to remove it.
              </p>
            </div>
            <Button size="sm" onClick={() => setUpgradeOpen(true)}
              style={{ background: 'hsl(var(--warning))', color: 'hsl(220 18% 4%)' }}
            >
              Upgrade Now
            </Button>
          </div>
        </motion.div>
      )}

      {/* Current plan */}
      {subscription && (
        <div className="reelia-card p-5 flex items-center justify-between">
          <div>
            <p className="text-xs font-medium mb-0.5" style={{ color: 'hsl(var(--foreground-subtle))' }}>CURRENT PLAN</p>
            <p className="text-base font-semibold" style={{ color: 'hsl(var(--foreground))' }}>{currentPlan?.name}</p>
            <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>${currentPlan?.price}/month</p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground-muted))' }}>
              <Download size={13} className="mr-1" /> Invoice
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setUpgradeOpen(true)}
              style={{ border: '1px solid hsl(var(--primary) / 0.4)', color: 'hsl(var(--primary))' }}
            >
              Change Plan
            </Button>
          </div>
        </div>
      )}

      {/* Watermark toggle */}
      <div className="reelia-card p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Reelia Watermark</p>
            <p className="text-xs mt-1 max-w-md" style={{ color: 'hsl(var(--foreground-muted))' }}>
              During your free trial, all exported videos include a small "Reelia" watermark badge in the corner.
              Upgrading to any paid plan removes it permanently.
            </p>
            {!subscription && (
              <div className="mt-2 px-2 py-1 rounded inline-flex items-center gap-1.5"
                style={{ background: 'hsl(var(--warning) / 0.1)', border: '1px solid hsl(var(--warning) / 0.3)' }}
              >
                <span className="text-xs" style={{ color: 'hsl(var(--warning))' }}>Active during free trial</span>
              </div>
            )}
          </div>
          <Switch
            checked={watermarkEnabled && !subscription}
            disabled={!subscription ? true : false}
            onCheckedChange={setWatermarkEnabled}
          />
        </div>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3">
        <span className="text-sm" style={{ color: billing === 'monthly' ? 'hsl(var(--foreground))' : 'hsl(var(--foreground-subtle))' }}>Monthly</span>
        <button
          onClick={() => setBilling(b => b === 'monthly' ? 'yearly' : 'monthly')}
          className="w-12 h-6 rounded-full relative"
          style={{ background: billing === 'yearly' ? 'hsl(var(--primary))' : 'hsl(var(--surface-hover))' }}
        >
          <span className="absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm"
            style={{ left: billing === 'yearly' ? '26px' : '4px', transition: 'left 0.2s' }}
          />
        </button>
        <span className="text-sm" style={{ color: billing === 'yearly' ? 'hsl(var(--foreground))' : 'hsl(var(--foreground-subtle))' }}>Yearly</span>
        {billing === 'yearly' && (
          <Badge className="badge-success text-xs border rounded-full">Save 20%</Badge>
        )}
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PLANS.map((plan, idx) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.08 }}
            className="reelia-card-glow p-5 flex flex-col relative"
            style={{
              borderColor: plan.highlight ? 'hsl(var(--primary) / 0.5)' : 'hsl(var(--border))',
              background: plan.highlight ? 'hsl(var(--surface-elevated))' : 'hsl(var(--surface))',
            }}
          >
            {plan.highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ background: 'hsl(var(--primary))', color: '#fff' }}
                >
                  Most Popular
                </span>
              </div>
            )}

            <div className="mb-4">
              <p className="text-base font-semibold" style={{ color: 'hsl(var(--foreground))' }}>{plan.name}</p>
              <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--foreground-muted))' }}>{plan.description}</p>
            </div>

            <div className="mb-4">
              <span className="text-3xl font-bold" style={{ color: 'hsl(var(--foreground))' }}>
                ${billing === 'yearly' ? Math.floor(plan.price * 0.8) : plan.price}
              </span>
              <span className="text-sm ml-1" style={{ color: 'hsl(var(--foreground-muted))' }}>/mo</span>
              {billing === 'yearly' && (
                <span className="text-xs ml-2 line-through" style={{ color: 'hsl(var(--foreground-subtle))' }}>${plan.price}</span>
              )}
            </div>

            <div className="space-y-2 flex-1 mb-5">
              {plan.features.map(feature => (
                <div key={feature} className="flex items-start gap-2">
                  <Check size={12} className="mt-0.5 flex-shrink-0" style={{ color: plan.highlight ? 'hsl(var(--primary))' : 'hsl(var(--success))' }} />
                  <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>{feature}</span>
                </div>
              ))}
            </div>

            <Button
              className="w-full mt-auto"
              size="sm"
              onClick={() => { setSelectedPlan(plan.id); setUpgradeOpen(true); }}
              style={{
                background: plan.highlight ? 'hsl(var(--primary))' : 'hsl(var(--surface-hover))',
                color: plan.highlight ? '#fff' : 'hsl(var(--foreground-muted))',
                border: plan.highlight ? 'none' : '1px solid hsl(var(--border))'
              }}
            >
              {subscription === plan.id ? 'Current Plan' : 'Get Started'}
            </Button>
          </motion.div>
        ))}
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade to {PLANS.find(p => p.id === selectedPlan)?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="p-3 rounded-lg" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
              <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>
                {PLANS.find(p => p.id === selectedPlan)?.name} Plan
              </p>
              <p className="text-2xl font-bold mt-1" style={{ color: 'hsl(var(--foreground))' }}>
                ${PLANS.find(p => p.id === selectedPlan)?.price}<span className="text-sm font-normal" style={{ color: 'hsl(var(--foreground-muted))' }}>/month</span>
              </p>
            </div>
            <div className="p-3 rounded-lg" style={{ background: 'hsl(var(--primary) / 0.06)', border: '1px solid hsl(var(--primary) / 0.2)' }}>
              <p className="text-xs font-medium mb-1" style={{ color: 'hsl(var(--primary))' }}>What you get:</p>
              <p className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>No watermark on exports · All features unlocked · Priority support</p>
            </div>
            <input className="reelia-input" placeholder="Card number **** **** **** 4242" />
            <div className="grid grid-cols-2 gap-2">
              <input className="reelia-input" placeholder="MM/YY" />
              <input className="reelia-input" placeholder="CVV" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setUpgradeOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleUpgrade}
              style={{ background: 'hsl(var(--primary))', color: '#fff' }}
            >
              <CreditCard size={13} className="mr-1" /> Subscribe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
